/**
 * The order and grouping of "Cases Needing Action" (Raquel, 14 Sep 2026).
 *
 * She reported the list as unusable: 1,095 rows, and in her screenshot a hearing from 24 August sat
 * beside one set for 15 February next year. The cause was that the list filtered on case status
 * alone, with **no date filter and no ordering at all**.
 *
 * ## Why it is not simply "the next two weeks"
 *
 * That is what she asked for, and taken literally it would have hidden the very rows she marked.
 * Counted on production the same day:
 *
 *   4,183 active cases have a court date that has already PASSED
 *   1,509 have no court date at all
 *     ~245 have a date still ahead
 *
 * A two-week window would therefore show almost nothing, and would quietly drop four thousand cases
 * whose hearing has been and gone with the case never updated. Those are not less urgent than an
 * upcoming hearing; they are a hearing somebody may have missed.
 *
 * So the list is grouped rather than filtered:
 *
 *  - **Overdue** — the court date has passed. Most recent first, because yesterday's hearing is the
 *    one still worth chasing today.
 *  - **Upcoming** — inside the window, soonest first. This is the part she asked for.
 *  - **Undated** — no court date on file. Its own short list, because a case with no date is a
 *    different job (find out when it is) and sorting it against dated cases means inventing an order.
 *
 * Anything beyond the window is counted and not listed, so the count stays honest without the list
 * growing back to a thousand rows.
 *
 * Pure. Tested by `npm run test-cases-needing-action`.
 */

/** Only the fields the grouping reads, so the tests do not have to build a whole case. */
export interface ActionableCase {
  ticket: { Ticket_CourtDate?: string | number | Date | null };
}

export const UPCOMING_WINDOW_DAYS = 14;

export interface GroupedCases<T> {
  /** Court date in the past, most recent first. */
  overdue: T[];
  /** Court date inside the window, soonest first. */
  upcoming: T[];
  /** No usable court date. */
  undated: T[];
  /** Dated beyond the window. Counted, deliberately not listed. */
  laterCount: number;
}

/**
 * The court date in epoch milliseconds, or null when there is not a usable one.
 *
 * `Ticket_CourtDate` arrives as a string, a number or a Date depending on the route, and an
 * unparseable value must read as "no date" rather than as 1970 — which would sort it to the top of
 * the overdue list forever.
 */
export function courtDateMs(value: string | number | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;

  // A string of digits is epoch milliseconds, and `new Date('1789862400000')` is **Invalid Date**,
  // not the epoch: the constructor reads a string as a date string. `CourtEvent_Start` is a bigint
  // that node-postgres hands back as a string for exactly this reason, and the appearance notifier
  // shipped broken on this once already.
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const epoch = Number(value.trim());
    return Number.isFinite(epoch) ? epoch : null;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * Split the cases into the three lists staff actually work from.
 *
 * `now` is passed in rather than read here so the boundary is testable. The comparison is against
 * the start of today, not the current instant: a hearing at 9am is still today's work at 2pm, and
 * treating it as overdue five hours after it started would move it out from under whoever is
 * dealing with it.
 */
export function groupCasesNeedingAction<T extends ActionableCase>(
  cases: readonly T[],
  now: Date = new Date(),
  windowDays: number = UPCOMING_WINDOW_DAYS,
): GroupedCases<T> {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const from = startOfToday.getTime();
  const until = from + windowDays * 24 * 60 * 60 * 1000;

  const overdue: Array<{ item: T; at: number }> = [];
  const upcoming: Array<{ item: T; at: number }> = [];
  const undated: T[] = [];
  let laterCount = 0;

  for (const item of cases) {
    const at = courtDateMs(item.ticket.Ticket_CourtDate);
    if (at === null) {
      undated.push(item);
    } else if (at < from) {
      overdue.push({ item, at });
    } else if (at <= until) {
      upcoming.push({ item, at });
    } else {
      laterCount += 1;
    }
  }

  return {
    overdue: overdue.sort((a, b) => b.at - a.at).map((row) => row.item),
    upcoming: upcoming.sort((a, b) => a.at - b.at).map((row) => row.item),
    undated,
    laterCount,
  };
}
